import { useState, useRef, useEffect } from 'react';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import LinearProgress from '@material-ui/core/LinearProgress';
import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import {
  OkrTreeNode,
  OkrObjective,
  GroupLead,
  formatQuarter,
  ownerGroupSlug,
  formatGroupName,
} from '../../hooks/useOkrEntities';
import { PersonRow } from '../PersonRow';

const KR_BADGE: Record<string, string> = {
  green: '#147f42',
  yellow: '#7a5500',
  red: '#9e1a10',
};

const CARD_WIDTH = 360;
const CARD_GAP = 16;

function objectiveProgress(obj: OkrObjective): number {
  const krs = obj.keyResults.filter(kr => kr.progress !== undefined);
  if (!krs.length) return 0;
  return Math.round(krs.reduce((s, kr) => s + (kr.progress ?? 0), 0) / krs.length);
}

const useStyles = makeStyles(theme => ({
  sectionHeader: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  sectionLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.8px',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase' as const,
  },
  sectionDesc: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
  carouselWrapper: {
    position: 'relative' as const,
    marginBottom: theme.spacing(3),
  },
  arrow: {
    position: 'absolute' as const,
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    '&:hover': { backgroundColor: theme.palette.background.paper },
  },
  arrowLeft: { left: -20 },
  arrowRight: { right: -20 },
  viewport: {
    overflow: 'hidden' as const,
    margin: '0 24px',
  },
  carousel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: CARD_GAP,
    padding: '8px 4px 20px',
    scrollBehavior: 'smooth' as const,
    overflowX: 'hidden' as const,
  },
  card: {
    minWidth: CARD_WIDTH,
    maxWidth: CARD_WIDTH,
    padding: theme.spacing(2.5),
    border: '2px solid #5f016f',
    display: 'flex',
    flexDirection: 'column' as const,
    boxSizing: 'border-box' as const,
    flexShrink: 0,
    cursor: 'pointer',
    transition: 'box-shadow 0.2s, opacity 0.2s',
  },
  cardSelected: {
    boxShadow: '0 4px 24px rgba(95,1,111,0.22)',
  },
  cardDimmed: {
    opacity: 0.55,
    border: '2px solid #c9a8d0',
  },
  cardHeader: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  logoRow: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'center',
  },
  quarterChip: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: '#f0e6f4',
    color: '#5f016f',
    borderRadius: 20,
    padding: '2px 10px',
    fontSize: 10,
    fontWeight: 600,
  },
  objectiveLabel: {
    display: 'block',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.8px',
    textTransform: 'uppercase' as const,
    color: theme.palette.text.secondary,
    textAlign: 'center' as const,
    marginBottom: theme.spacing(0.5),
  },
  objectiveTitle: {
    fontSize: '0.95rem',
    fontWeight: 700,
    lineHeight: 1.35,
    marginBottom: theme.spacing(2),
  },
  progressLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5),
  },
  bar: {
    height: 5,
    borderRadius: 3,
    marginBottom: theme.spacing(1.5),
    backgroundColor: '#e0e0e0',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    marginTop: 'auto',
    paddingTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  krItem: {
    marginBottom: theme.spacing(1),
  },
  krTitleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 6,
    marginBottom: 3,
  },
  krBadge: {
    fontSize: 9,
    fontWeight: 700,
    color: '#fff',
    borderRadius: 3,
    padding: '1px 5px',
    flexShrink: 0,
    marginTop: 1,
    letterSpacing: '0.3px',
  },
  krTitle: {
    fontSize: '0.78rem',
    lineHeight: 1.3,
    flex: 1,
    minWidth: 0,
  },
  krValue: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap' as const,
    marginLeft: 4,
  },
  krBar: {
    height: 3,
    borderRadius: 2,
    backgroundColor: '#e0e0e0',
    marginLeft: 30,
  },
}));

function ArriveLogotype({ color }: { color: string }) {
  return (
    <svg width="130" height="54" viewBox="0 0 1708.5 715.46" xmlns="http://www.w3.org/2000/svg">
      <path fill={color} d="M833.32,238.6c-18,0-32.4,14.8-32.4,32.8s14.4,32.8,32.4,32.8,32.8-15.2,32.8-32.8-15.2-32.8-32.8-32.8Z"/>
      <rect fill={color} x="806.12" y="321" width="54.8" height="200"/>
      <path fill={color} d="M589.93,336.2v-15.2h-54.8v200h54.8v-110c0-24.4,18.8-41.2,43.6-41.2,7.6,0,13.6,1.2,19.6,2.4v-56s-3.2-.4-7.2-.4c-25.6,0-44,8.8-56,20.4Z"/>
      <path fill={color} d="M725.42,336.2v-15.2h-54.8v200h54.8v-110c0-24.4,18.8-41.2,43.6-41.2,7.6,0,13.6,1.2,19.6,2.4v-56s-3.2-.4-7.2-.4c-25.6,0-44,8.8-56,20.4Z"/>
      <path fill={color} d="M353.48,386.07c-8.14-.87-16.4-1.32-24.75-1.32-26.09,0-51.2,4.24-74.62,12.61l18.84,52.73c13.8-4.93,28.49-7.92,43.8-8.94l-53.22,79.83h65.2l48.86-73.21c14.7,4.45,28.12,10.95,39.57,19.3,20.61,15.03,31.96,34.18,31.96,53.91h56V245h-57.6l-94.05,141.08ZM449.13,421.1c-11.94-8.57-25.22-15.74-39.46-21.37l39.46-59.13v80.5Z"/>
      <polygon fill={color} points="1016.42 321 974.42 455.4 932.02 321 874.42 321 937.62 520.99 1012.02 520.99 1075.22 321 1016.42 321"/>
      <path fill={color} d="M1171.32,314.6c-62.4,0-103.6,46.8-103.6,105.6s44,107.2,107.2,107.2c50.8,0,89.2-30.8,96.8-72.8h-58.8c-5.6,14.4-22,20-39.2,20-28,0-43.2-13.2-48.4-36.8h148.4c1.2-7.2,1.6-14.4,1.6-20,0-57.2-41.6-103.2-104-103.2ZM1125.32,399.8c5.6-24,23.2-35.2,46.4-35.2,26,0,40.8,14.8,45.2,35.2h-91.6Z"/>
      <path fill={color} d="M1421.99,264.55c-23.09,0-37.53-25-25.98-45l18.18-31.48h-15.82l-18.18,31.49c-16.08,27.84-45.79,45-77.94,45h-32.4l-7.91,13.7h32.4c23.09,0,37.53,25,25.98,45l-18.18,31.48h15.82l18.18-31.49c16.08-27.84,45.79-44.99,77.94-44.99h32.4l7.91-13.7h-32.4Z"/>
    </svg>
  );
}

interface CompanyObjectiveCardProps {
  objective: OkrObjective;
  index: number;
  total: number;
  selected: boolean;
  quarter: string;
  groupLead?: GroupLead;
  onClick: () => void;
  /** When set, renders this text as the card identity instead of the Arrive logotype */
  groupTitle?: string;
}

function CompanyObjectiveCard({
  objective,
  index,
  total,
  selected,
  quarter,
  groupLead,
  onClick,
  groupTitle,
}: CompanyObjectiveCardProps) {
  const classes = useStyles();
  const theme = useTheme();
  const logoColor = theme.palette.type === 'dark' ? '#fff' : '#5f016f';
  const [expanded, setExpanded] = useState(false);
  const progress = objectiveProgress(objective);

  const cardClass = [
    classes.card,
    selected ? classes.cardSelected : classes.cardDimmed,
  ].join(' ');

  return (
    <Paper className={cardClass} elevation={selected ? 4 : 1} onClick={onClick}>
      <Box className={classes.cardHeader}>
        {/* Row 1: avatar + name side by side, right-aligned */}
        <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {groupLead && (
            <PersonRow
              accountable={groupLead}
              avatarSize={22}
              nameStyle={{ fontSize: 10, fontWeight: 600 }}
            />
          )}
        </Box>
        {/* Row 2: logo/group name centered, quarter chip right */}
        <Box className={classes.logoRow}>
          <span />
          {groupTitle ? (
            <Typography style={{ fontWeight: 700, fontSize: '1.05rem', letterSpacing: '-0.3px', color: logoColor }}>
              {groupTitle}
            </Typography>
          ) : (
            <ArriveLogotype color={logoColor} />
          )}
          <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box component="span" className={classes.quarterChip}>{formatQuarter(quarter)}</Box>
          </Box>
        </Box>
      </Box>

      <span className={classes.objectiveLabel}>Objective {index + 1} of {total}</span>
      <Typography className={classes.objectiveTitle}>
        {objective.title}
      </Typography>

      {progress > 0 && (
        <Box>
          <Box className={classes.progressLabel}>
            <Typography variant="caption" color="textSecondary">PROGRESS</Typography>
            <Typography variant="caption"><strong>{progress}%</strong></Typography>
          </Box>
          <LinearProgress variant="determinate" value={progress} className={classes.bar} />
        </Box>
      )}

      <Box
        className={classes.footer}
        onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
      >
        <Typography variant="caption" color="textSecondary">
          KEY RESULTS &nbsp;·&nbsp; {objective.keyResults.length} key results
        </Typography>
        <Box display="flex" alignItems="center">
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Box mt={0.5}>
          {objective.keyResults.map((kr, i) => (
            <Box key={i} className={classes.krItem}>
              <Box className={classes.krTitleRow}>
                <span
                  className={classes.krBadge}
                  style={{ backgroundColor: KR_BADGE[kr.confidence] ?? '#555' }}
                >
                  KR{i + 1}
                </span>
                <Typography className={classes.krTitle}>{kr.title}</Typography>
                {kr.progress !== undefined && (
                  <Typography variant="caption" style={{ fontWeight: 700, color: '#5f016f', whiteSpace: 'nowrap' as const, flexShrink: 0 }}>
                    {kr.progress}%
                  </Typography>
                )}
              </Box>
              {kr.progress !== undefined && (
                <LinearProgress
                  variant="determinate"
                  value={kr.progress}
                  className={classes.krBar}
                />
              )}
            </Box>
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
}

interface CompanyOkrBannerProps {
  node: OkrTreeNode;
  selectedIndex: number;
  onSelect: (i: number) => void;
  groupLeads?: Map<string, GroupLead>;
  /** When set, shows this text instead of the Arrive logotype (use for non-company entities) */
  groupTitle?: string;
  /** Override the small section label (default: "Group") */
  sectionLabel?: string;
  /** Override the section description (default: "Company OKR · <name>") */
  sectionDesc?: string;
}

export function CompanyOkrBanner({ node, selectedIndex, onSelect, groupLeads, groupTitle, sectionLabel, sectionDesc }: CompanyOkrBannerProps) {
  const classes = useStyles();
  const trackRef = useRef<HTMLDivElement>(null);
  const { entity } = node;
  const groupSlug = ownerGroupSlug(entity);
  const groupLead = groupLeads?.get(groupSlug);
  const count = entity.spec.objectives.length;

  useEffect(() => {
    if (!trackRef.current) return;
    const containerWidth = trackRef.current.parentElement?.offsetWidth ?? 0;
    const targetScroll =
      selectedIndex * (CARD_WIDTH + CARD_GAP) - (containerWidth / 2 - CARD_WIDTH / 2);
    trackRef.current.scrollTo({ left: Math.max(0, targetScroll), behavior: 'smooth' });
  }, [selectedIndex]);

  return (
    <Box>
      <Box className={classes.sectionHeader}>
        <Typography className={classes.sectionLabel}>{sectionLabel ?? 'Group'}</Typography>
        <Typography className={classes.sectionDesc}>
          {sectionDesc ?? <>Company OKR &nbsp;·&nbsp; {formatGroupName(groupSlug)}</>}
        </Typography>
      </Box>

      <Box className={classes.carouselWrapper}>
        <IconButton
          className={`${classes.arrow} ${classes.arrowLeft}`}
          size="small"
          disabled={selectedIndex === 0}
          onClick={() => onSelect(selectedIndex - 1)}
        >
          <ChevronLeftIcon />
        </IconButton>

        <Box className={classes.viewport}>
          <div ref={trackRef} className={classes.carousel}>
            {entity.spec.objectives.map((obj, i) => (
              <CompanyObjectiveCard
                key={i}
                index={i}
                total={count}
                objective={obj}
                selected={selectedIndex === i}
                quarter={entity.spec.quarter}
                groupLead={groupLead}
                onClick={() => onSelect(i)}
                groupTitle={groupTitle}
              />
            ))}
          </div>
        </Box>

        <IconButton
          className={`${classes.arrow} ${classes.arrowRight}`}
          size="small"
          disabled={selectedIndex === count - 1}
          onClick={() => onSelect(selectedIndex + 1)}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
